import { Controller } from "@hotwired/stimulus"

// Adds thousands-separator commas to an input as the user types.
// Strips commas on submit via a hidden mirror input, so the server still
// receives a clean numeric string.
//
// Usage:
//   <input type="text" data-controller="number-format" data-number-format-name-value="..." inputmode="numeric">
//
// The visible <input> is name="" (not submitted). A hidden <input> with the
// real name is kept in sync.
export default class extends Controller {
  static values = {
    name: String,
    decimals: { type: Number, default: 0 }
  }

  connect() {
    this.hidden = document.createElement("input")
    this.hidden.type = "hidden"
    this.hidden.name = this.nameValue
    this.hidden.value = this.stripCommas(this.element.value)
    this.element.after(this.hidden)
    this.element.removeAttribute("name")
    this.element.value = this.format(this.element.value)
    this.element.addEventListener("input", this.onInput)
    this.element.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput)
    this.element.removeEventListener("blur", this.onBlur)
    this.hidden?.remove()
  }

  onInput = (e) => {
    const cursor = e.target.selectionStart
    const before = e.target.value
    const raw = this.stripCommas(before)
    const formatted = this.format(raw)
    e.target.value = formatted

    // Try to preserve cursor position relative to the right edge of digits
    const delta = formatted.length - before.length
    const next = Math.max(0, cursor + delta)
    e.target.setSelectionRange(next, next)

    this.hidden.value = raw
  }

  onBlur = () => {
    this.hidden.value = this.stripCommas(this.element.value)
  }

  stripCommas(v) {
    return (v ?? "").toString().replace(/,/g, "")
  }

  format(v) {
    if (v === "" || v == null) return ""
    const raw = this.stripCommas(v)
    // Allow a trailing decimal point or partial decimals while typing
    const match = raw.match(/^(-?\d*)(\.\d*)?$/)
    if (!match) return raw
    const intPart = match[1] || ""
    const decPart = match[2] || ""
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return withCommas + decPart
  }
}
